import type { MessengerComposerDraft, MessengerMessage } from '@/api/client';

type ResolveThreadComposerDraftStateArgs = {
  draft: MessengerComposerDraft | undefined;
  cachedMessages: MessengerMessage[];
  cacheHydrated: boolean;
  pendingForwardMessages: MessengerMessage[];
};

export type ThreadComposerDraftReferenceIds = {
  replyToMessageId: string | null;
  forwardMessageIds: string[];
};

export type ResolvedThreadComposerDraftState = {
  text: string;
  replyTo: MessengerMessage | null;
  forwardMessages: MessengerMessage[];
  pendingForwardsWon: boolean;
  referenceIds: ThreadComposerDraftReferenceIds;
};

export type ThreadComposerEditStash = {
  text: string;
  replyTo: MessengerMessage | null;
  forwardMessages: MessengerMessage[];
  referenceIds: ThreadComposerDraftReferenceIds;
};

export function resolveThreadComposerDraftState({
  draft,
  cachedMessages,
  pendingForwardMessages,
}: ResolveThreadComposerDraftStateArgs): ResolvedThreadComposerDraftState {
  const messagesById = new Map<string, MessengerMessage>();
  for (const message of cachedMessages) messagesById.set(message.id, message);
  for (const message of pendingForwardMessages) messagesById.set(message.id, message);

  const replyTo = draft?.reply_to_message_id
    ? (messagesById.get(draft.reply_to_message_id) ?? null)
    : null;
  const pendingForwardsWon = pendingForwardMessages.length > 0;
  const forwardMessages = pendingForwardsWon
    ? [...pendingForwardMessages]
    : (draft?.forward_message_ids ?? []).flatMap((id) => {
        const message = messagesById.get(id);
        return message ? [message] : [];
      });
  const referenceIds: ThreadComposerDraftReferenceIds = {
    replyToMessageId: draft?.reply_to_message_id ?? null,
    forwardMessageIds: pendingForwardsWon
      ? pendingForwardMessages.map((message) => message.id)
      : [...(draft?.forward_message_ids ?? [])],
  };

  return {
    text: draft?.text ?? '',
    replyTo,
    forwardMessages,
    pendingForwardsWon,
    referenceIds,
  };
}

export function createThreadComposerEditStash(
  state: ThreadComposerEditStash
): ThreadComposerEditStash {
  return {
    ...state,
    forwardMessages: [...state.forwardMessages],
    referenceIds: {
      replyToMessageId: state.referenceIds.replyToMessageId,
      forwardMessageIds: [...state.referenceIds.forwardMessageIds],
    },
  };
}
